import { useState } from 'react';
import type { ReactElement, VoidFunctionComponent } from 'react';

import { window } from '../../../../../globals';
import { hardRedirectTo } from '../../../../../utils';
import Button, { BUTTON_TYPE } from '../../../../generic/Button';
import Flex from '../../../../generic/Flex';
import Loader from '../../../../generic/Loader';
import Text from '../../../../generic/Text';
import type { PendingDomainToken } from '../../../../../../typings/api';
import type { OrganizationSsoMethod } from '../../../../../../typings/enums';

import { usePendingDomains, useResendingEmailTokenIds } from './hooks';

enum EmailResendStatus {
  Default,
  Resending,
  Resent,
}

type PendingDomainRowProps = {
  token: PendingDomainToken;
  resendStatus: EmailResendStatus;
  resendEmail(): void;
};

/**
 * Display a list of pending domain that are waiting for the user to verify their email address.
 */
const PendingDomainRow = ({ token, resendStatus, resendEmail }: PendingDomainRowProps) => {
  let nextStep: string;
  let action: ReactElement;

  if (token.meta.authenticated) {
    nextStep = `Check your email (${token.email}) and click the verification link.`;
    if (resendStatus === EmailResendStatus.Default) {
      action = (
        <Button size="small" type={BUTTON_TYPE.SECONDARY} onClick={resendEmail}>
          Resend Email
        </Button>
      );
    } else if (resendStatus === EmailResendStatus.Resending) {
      action = (
        <Button size="small" type={BUTTON_TYPE.SECONDARY} isDisabled onClick={() => {}}>
          Sending...
        </Button>
      );
    } else {
      action = <span className="pending-domains__row__email-sent-message">Sent</span>;
    }
  } else {
    nextStep = 'Use your provided SAML configuration to authenticate.';
    action = (
      <Button size="small" type={BUTTON_TYPE.SECONDARY} onClick={() => hardRedirectTo(token.samlRedirectUrl)}>
        Sign in with SSO
      </Button>
    );
  }

  return (
    <div className="pending-domains__row">
      <Flex justify="space-between">
        <div>
          <div className="pending-domains__row__domain-name">{token.meta.emailDomain}</div>
          <Text>{`Next step: ${nextStep}`}</Text>
        </div>
        {action}
      </Flex>
    </div>
  );
};

const THREE_SECONDS = 3_000;

type Props = {
  method: OrganizationSsoMethod;
};
const PendingDomains: VoidFunctionComponent<Props> = ({ method }) => {
  const organizationId = window.user?.organizationId as number;
  const { value: pendingOrgDomains, isLoading } = usePendingDomains(organizationId);
  const { resendEmailToken, tokenIds } = useResendingEmailTokenIds(organizationId);
  const [resentTokenIds, setResentTokenIds] = useState<number[]>([]);

  const filteredTokens = (pendingOrgDomains || []).filter(({ meta }) => meta.ssoMethod === method);

  if (!filteredTokens.length) {
    return null;
  }

  if (isLoading) {
    return <Loader />;
  }

  const tokenResendStatus = (tokenId: number): EmailResendStatus => {
    if (tokenIds.includes(tokenId)) {
      return EmailResendStatus.Resending;
    }
    if (resentTokenIds.includes(tokenId)) {
      return EmailResendStatus.Resent;
    }
    return EmailResendStatus.Default;
  };

  const resendTokenEmail = async (tokenId: number) => {
    try {
      await resendEmailToken(tokenId);

      setResentTokenIds((prevState) => [...prevState, tokenId]);
      setTimeout(() => {
        // This tokenId will be removed from the resentEmailTokenIds array in 3 seconds.
        setResentTokenIds((prevState) => prevState.filter((id) => id !== tokenId));
      }, THREE_SECONDS);
    } catch (e) {
      // Error is handled by Redux.
    }
  };

  return (
    <div className="margin-top-lg">
      <Text.H4>Pending Domains</Text.H4>
      <div className="pending-domains">
        {filteredTokens.map((token) => (
          <PendingDomainRow
            key={token.id}
            token={token}
            resendStatus={tokenResendStatus(token.id)}
            resendEmail={() => resendTokenEmail(token.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default PendingDomains;
